import React from "react";
import * as Yup from "yup";
import { FormikErrors, FormikValues } from "formik";
import { History } from "history";
import { format, parse } from "date-fns";
import styled from "styled-components";
import { components } from "react-select";

import {
  BOOKING_REGISTER_URSSAF_CLIENT_ENDPOINT,
  FRENCH_COMMUNES_ENDPOINT,
  TERRITORIES_ENDPOINT,
  FRENCH_POSTAL_CODES_ENDPOINT,
} from "@config/endpoints";
import { ROUTES } from "@config/app.routing";
import { COUNTRY_BY_INSEE_CODE } from "@config/consts";

import { FrenchCommune, FrenchPostalCode } from "@typings/globals";

import { Column } from "@ui/Atoms/Grid";
import { TestWrapper } from "@ui/Atoms";

import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import {
  DATE_FORMAT,
  DAY_PICKER_VALID_DATE_REGEX,
} from "@services/DateFormatter";
import { buildUrl } from "@services/BuildUrl";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { stringifyFalsies } from "@services/StringifyFalsies";

import { showToast } from "@containers/Toastify";

import { TranslateFunction } from "@hooks/useTranslate";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";

const { Option, SingleValue } = components;

const myCleaningsRoute = `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`;

enum FIELDS {
  GENDER = "gender",
  BIRTH_NAME = "birthName",
  SURNAME = "surname",
  FIRST_NAME = "firstName",
  BIRTH_DATE = "birthDate",
  MOBILE_PHONE = "mobilePhone",
  EMAIL = "email",
  BIRTH_PLACE_COMMUNE = "birthPlaceCommune",
  BIRTH_PLACE_COUNTRY_CODE = "birthPlaceCountryCode",
  LANE_TYPE = "laneType",
  LANE_NUMBER = "laneNumber",
  ROAD_NAME = "roadName",
  LANE_LETTER = "laneLetter",
  ADDITIONAL_ADDRESS = "additionalAddress",
  POSTAL_CODE = "postalCode",
  BIC = "bic",
  IBAN = "iban",
  ACCOUNT_HOLDER_TITLE = "accountHolderTitile",
  ACCOUNT_HOLDER_FIRSTNAME = "accountHolderFirstName",
  ACCOUNT_HOLDER_LASTNAME = "accountHolderLastName",
}

const initialValues = {
  [FIELDS.GENDER]: "", // dropdown
  [FIELDS.BIRTH_NAME]: "",
  [FIELDS.SURNAME]: "",
  [FIELDS.FIRST_NAME]: "",
  [FIELDS.BIRTH_DATE]: "",
  [FIELDS.MOBILE_PHONE]: "",
  [FIELDS.EMAIL]: "",
  /** birthPlace */
  [FIELDS.BIRTH_PLACE_COUNTRY_CODE]: "", // dropdown
  [FIELDS.BIRTH_PLACE_COMMUNE]: "", // dropdown
  /** address */
  [FIELDS.POSTAL_CODE]: "", // dropdown
  [FIELDS.LANE_TYPE]: "", // dropdown
  [FIELDS.LANE_NUMBER]: "",
  [FIELDS.ROAD_NAME]: "",
  [FIELDS.LANE_LETTER]: "",
  [FIELDS.ADDITIONAL_ADDRESS]: "",
  /** bankAccount */
  [FIELDS.ACCOUNT_HOLDER_TITLE]: "", // dropdown
  [FIELDS.ACCOUNT_HOLDER_FIRSTNAME]: "",
  [FIELDS.ACCOUNT_HOLDER_LASTNAME]: "",
  [FIELDS.BIC]: "",
  [FIELDS.IBAN]: "",
};

const getValidationSchema = (translate: TranslateFunction): Yup.ObjectSchema =>
  Yup.object().shape({
    [FIELDS.GENDER]: Yup.string().required(translate("validate_is_required")),
    [FIELDS.BIRTH_NAME]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.SURNAME]: Yup.string().required(translate("validate_is_required")),
    [FIELDS.FIRST_NAME]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.BIRTH_DATE]: Yup.string()
      .required(translate("validate_is_required"))
      .trim()
      .matches(DAY_PICKER_VALID_DATE_REGEX, {
        message: translate("validate_date"),
      }),
    /** birthPlace */
    [FIELDS.BIRTH_PLACE_COUNTRY_CODE]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.BIRTH_PLACE_COMMUNE]: Yup.string().when(
      FIELDS.BIRTH_PLACE_COUNTRY_CODE,
      {
        is: COUNTRY_BY_INSEE_CODE.FRANCE,
        then: Yup.string().required(translate("validate_is_required")),
      },
    ),
    /** Address */
    [FIELDS.POSTAL_CODE]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.LANE_TYPE]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.LANE_NUMBER]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.ROAD_NAME]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.MOBILE_PHONE]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.EMAIL]: Yup.string()
      .email(translate("email_wrong_format"))
      .required(translate("validate_is_required")),
    /** Bank Account */
    [FIELDS.ACCOUNT_HOLDER_TITLE]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.ACCOUNT_HOLDER_FIRSTNAME]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.ACCOUNT_HOLDER_LASTNAME]: Yup.string().required(
      translate("validate_is_required"),
    ),
    [FIELDS.BIC]: Yup.string().required(translate("validate_is_required")),
    [FIELDS.IBAN]: Yup.string().required(translate("validate_is_required")),
  });

const fetchCountries = async (): Promise<AsyncActionResult> => {
  return await asyncActionCreator({
    action: "",
    url: buildUrl(TERRITORIES_ENDPOINT)
      .addQuery("preferredTerritoryName", "France")
      .done(),
  });
};

const fetchCommunes = async (input: string): Promise<AsyncActionResult> => {
  return await asyncActionCreator({
    action: "",
    url: buildUrl(FRENCH_COMMUNES_ENDPOINT)
      .setQuery("communeName", input)
      .done(),
  });
};

const fetchPostalCodes = async (input: string): Promise<AsyncActionResult> => {
  return await asyncActionCreator({
    action: "",
    url: buildUrl(FRENCH_POSTAL_CODES_ENDPOINT)
      .setQuery("postalCode", input)
      .done(),
  });
};

interface HandleSubmit {
  history: History;
  values: FormikValues;
  communes: FrenchCommune[];
  postalCodes: FrenchPostalCode[];
  setSubmitting: (value: boolean) => void;
  setErrors: (erros: FormikErrors<FormikValues>) => void;
}

const handleSubmit = async ({
  history,
  values,
  communes,
  postalCodes,
  setSubmitting,
  setErrors,
}: HandleSubmit): Promise<void> => {
  const selectedPostalCode = postalCodes.find(
    postalCode => postalCode.inseeCode === values[FIELDS.POSTAL_CODE],
  );
  const selectedCommune = communes.find(
    commune => commune.inseeCode === values[FIELDS.BIRTH_PLACE_COMMUNE],
  );

  const addressPostalCode = stringifyFalsies(selectedPostalCode?.postalCode);
  const addressCommuneName = stringifyFalsies(
    selectedPostalCode?.communeName,
  ).toUpperCase();
  const addressCommuneCode = stringifyFalsies(selectedPostalCode?.inseeCode);

  const brithPlaceDepartment = stringifyFalsies(
    selectedCommune?.departmentCode,
  );
  const birthPlaceCommuneCode = stringifyFalsies(selectedCommune?.communeCode);
  const birthPlaceCommuneName = stringifyFalsies(selectedCommune?.communeName);

  const body = {
    gender: values[FIELDS.GENDER],
    birthName: values[FIELDS.BIRTH_NAME],
    surname: values[FIELDS.SURNAME],
    firstName: values[FIELDS.FIRST_NAME],
    birthDate: format(
      parse(
        values[FIELDS.BIRTH_DATE],
        DATE_FORMAT.DAY_PICKER_INPUT,
        new Date(),
      ),
      DATE_FORMAT.YEAR_MONTH_DAY,
    ),
    mobilePhone: values[FIELDS.MOBILE_PHONE],
    email: values[FIELDS.EMAIL],
    birthPlace: {
      countryCode: values[FIELDS.BIRTH_PLACE_COUNTRY_CODE],
      department: brithPlaceDepartment,
      commune: {
        code: birthPlaceCommuneCode,
        name: birthPlaceCommuneName,
      },
    },
    address: {
      laneNumber: values[FIELDS.LANE_NUMBER],
      laneLetter: values[FIELDS.LANE_LETTER],
      laneType: values[FIELDS.LANE_TYPE],
      roadName: values[FIELDS.ROAD_NAME],
      additionalAddress: values[FIELDS.ADDITIONAL_ADDRESS],
      communeName: addressCommuneName,
      communeCode: addressCommuneCode,
      postalCode: addressPostalCode,
    },
    bankAccount: {
      accountHolderTitle: values[FIELDS.ACCOUNT_HOLDER_TITLE],
      accountHolderFirstName: values[FIELDS.ACCOUNT_HOLDER_FIRSTNAME],
      accountHolderLastName: values[FIELDS.ACCOUNT_HOLDER_LASTNAME],
      bic: values[FIELDS.BIC],
      iban: values[FIELDS.IBAN],
    },
  };

  const { onSuccess, onError, mapApiErrorsToForm } = await asyncActionCreator({
    action: "",
    url: BOOKING_REGISTER_URSSAF_CLIENT_ENDPOINT,
    method: ASYNC_ACTION_TYPES.POST,
    body,
  });

  onSuccess(() => {
    setSubmitting(false);
    history.push(myCleaningsRoute);
  });

  mapApiErrorsToForm(setErrors, { preventGlobalError: true });

  onError(err => {
    setSubmitting(false);
    showToast(err.message, {
      type: "error",
    });
  });
};

const UrssafDropdownCustomSingleValue = (props: any) => {
  return (
    <StyledSingleValue {...props}>
      <OptionWrapper>
        <div>{props.data.label}</div>
        <ExtraLabel>{props.data.extraLabel}</ExtraLabel>
      </OptionWrapper>
    </StyledSingleValue>
  );
};

const UrssafDropdownCustomOption = (props: any) => {
  return (
    <Option {...props}>
      <TestWrapper testId={props.data.label}>
        <OptionWrapper>
          <span>{props.data.label}</span>
          <ExtraLabel>{props.data.extraLabel}</ExtraLabel>
        </OptionWrapper>
      </TestWrapper>
    </Option>
  );
};

const StyledColumn = styled(Column)<{ withOptionalLabel?: boolean }>`
  margin-bottom: ${props =>
    props.withOptionalLabel
      ? props.theme.margins.base_x3
      : props.theme.margins.base_x5};
`;

const Heading = styled.h2`
  font-size: ${props => props.theme.fonts.sizes.body};
  font-weight: ${props => props.theme.fonts.weights.semibold};
  margin-bottom: ${props => props.theme.margins.base_x5};
`;

const OptionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledSingleValue = styled(SingleValue)`
  width: 100%;
`;

const ExtraLabel = styled.div`
  color: ${props => props.theme.palette.grey600};
  font-size: ${props => props.theme.fonts.sizes.body2};
`;

const Subtitle = styled.span`
  color: ${props => props.theme.palette.grey600};
`;

export {
  initialValues,
  FIELDS,
  myCleaningsRoute,
  getValidationSchema,
  handleSubmit,
  UrssafDropdownCustomSingleValue,
  UrssafDropdownCustomOption,
  StyledColumn,
  Subtitle,
  Heading,
  OptionWrapper,
  StyledSingleValue,
  ExtraLabel,
  fetchCountries,
  fetchPostalCodes,
  fetchCommunes,
};
